import { app } from '../../env'
import { USER_JOURNEY_APPLICATION_NAME, USER_JOURNEY_PLATFORM_NAME } from './constants'
import { JourneyOutcome, JourneyTerminationReason, UserJourney } from './types'

export interface RecordCompletedJourneyPayload {
  journey_name: string
  journey_id: string
  entity_type: 'ENTITY_TYPE_UNSPECIFIED'
  app_token: 'none'
  platform_name: string
  platform_version: string
  device_model: string
  application_name: string
  application_version: string
  locale: string
  created_at: number
  ended_at: number
  duration_real_time_ms: number
  outcome: JourneyOutcome
  termination_reason?: JourneyTerminationReason
  variant?: string
  frustration_signals: string[]
  friction_signals: string[]
  tags: string[]
  session_id: string
  is_retry: boolean
}

export interface JourneyStartedEvent {
  journey_name: string
  journey_id: string
  variant?: string
  session_id: string
  application_name: string
  application_version: string
  platform_name: string
  created_at: number
}

const BROWSER_FAMILIES: Array<[RegExp, string]> = [
  [/Edg(?:A|iOS)?\/(\d+[\d.]*)/, 'edge'],
  [/(?:OPR|OPiOS|OPT)\/(\d+[\d.]*)/, 'opera'],
  [/SamsungBrowser\/(\d+[\d.]*)/, 'samsung'],
  [/(?:CriOS|Chrome)\/(\d+[\d.]*)/, 'chrome'],
  [/(?:FxiOS|Firefox)\/(\d+[\d.]*)/, 'firefox'],
  [/Version\/(\d+[\d.]*).*Safari/, 'safari'],
]

const OS_FAMILIES: Array<[RegExp, string]> = [
  [/iPhone|iPad|iPod|\biOS\b/, 'ios'],
  [/Macintosh.*Mobile\//, 'ios'],
  [/Android/, 'android'],
  [/Macintosh|Mac OS X/, 'macos'],
  [/Windows/, 'windows'],
  [/CrOS/, 'chromeos'],
  [/Linux/, 'linux'],
]

export const describeUserAgent = (ua: string, maxTouchPoints = 0): { os: string; browser: string; version: string } => {
  const family = OS_FAMILIES.find(([pattern]) => pattern.test(ua))?.[1] ?? 'other'
  const os = family === 'macos' && maxTouchPoints > 1 ? 'ios' : family
  for (const [pattern, browser] of BROWSER_FAMILIES) {
    const match = ua.match(pattern)
    if (match !== null) {
      return { os, browser, version: match[1] }
    }
  }
  return { os, browser: 'other', version: 'unknown' }
}

export const buildJourneyStartedEvent = (journey: UserJourney, esid: string): JourneyStartedEvent => ({
  journey_name: journey.name,
  journey_id: journey.id,
  variant: journey.variant,
  session_id: esid,
  application_name: USER_JOURNEY_APPLICATION_NAME,
  application_version: app.APP_VERSION,
  platform_name: USER_JOURNEY_PLATFORM_NAME,
  created_at: journey.created,
})

export const buildRecordCompletedJourneyPayload = (
  journey: UserJourney,
  outcome: JourneyOutcome,
  terminationReason: JourneyTerminationReason | undefined,
  esid: string,
  startedAtMonotonicMs?: number
): RecordCompletedJourneyPayload => {
  const wallMs = Date.now() - journey.created
  const duration = startedAtMonotonicMs === undefined ? wallMs : Math.max(wallMs, Math.round(performance.now() - startedAtMonotonicMs))
  const nav = typeof navigator === 'undefined' ? undefined : navigator
  const { os, browser, version } = describeUserAgent(nav?.userAgent ?? '', nav?.maxTouchPoints ?? 0)

  return {
    journey_name: journey.name,
    journey_id: journey.id,
    entity_type: 'ENTITY_TYPE_UNSPECIFIED',
    app_token: 'none',
    platform_name: USER_JOURNEY_PLATFORM_NAME,
    platform_version: `${browser}/${version}`,
    device_model: `${os}/${browser}`,
    application_name: USER_JOURNEY_APPLICATION_NAME,
    application_version: app.APP_VERSION,
    locale: nav?.language.toLowerCase() ?? '',
    created_at: journey.created,
    ended_at: journey.created + duration,
    duration_real_time_ms: duration,
    outcome,
    ...(outcome === JourneyOutcome.TERMINATED && terminationReason !== undefined ? { termination_reason: terminationReason } : {}),
    variant: journey.variant,
    frustration_signals: journey.frustrations,
    friction_signals: journey.frictions,
    tags: journey.tags,
    session_id: esid,
    is_retry: false,
  }
}
